import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import DeleteProjectDialog from "@/components/projects/DeleteProjectDialog";
import AutoAlignerDialog from "@/components/cat/AutoAlignerDialog";
import { autoAligner } from "@/lib/cat/autoAligner";
import { translationMemory } from "@/lib/cat/translationMemory";
import { supabase } from "@/lib/supabase";
import {
    Plus, Search, LayoutGrid, List, Clock, CheckCircle,
    Globe, FileText, Calendar, ArrowRight, Trash2,
    AlignJustify, ChevronDown, ChevronRight, SlidersHorizontal,
} from "lucide-react";

interface Project {
    id: string;
    name: string;
    source_language: string;
    target_language: string;
    total_segments: number;
    completed_segments: number;
    word_count: number;
    created_at: string;
    updated_at: string;
    file_name?: string;
    file_type?: string;
    file_count?: number;
}

const S_ALL = "all";
const S_NEW = "not_started";
const S_WIP = "in_progress";
const S_DONE = "complete";

const ProjectsPage: React.FC = () => {
    const { user } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPair, setSelectedPair] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState(S_ALL);
    const [viewMode, setViewMode] = useState<"table" | "grid" | "grouped">("table");
    const [alignerOpen, setAlignerOpen] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean; projectId: string | null; projectName: string;
    }>({ isOpen: false, projectId: null, projectName: "" });
    const [isDeleting, setIsDeleting] = useState(false);

    const loadProjects = async () => {
        if (!user) return;
        try {
            const { data, error } = await supabase
                .from("cat_projects").select("*")
                .eq("owner_id", user.id)
                .order("created_at", { ascending: false });
            if (!error) setProjects(data || []);
        } finally { setLoading(false); }
    };

    const location = useLocation();
    useEffect(() => { if (user) loadProjects(); }, [user, location.key]);


    const handleDeleteProject = async () => {
        if (!deleteDialog.projectId) return;
        setIsDeleting(true);
        try {
            await supabase.from("cat_segments").delete().eq("project_id", deleteDialog.projectId);
            await supabase.from("cat_projects").delete().eq("id", deleteDialog.projectId);
            setDeleteDialog({ isOpen: false, projectId: null, projectName: "" });
            await loadProjects();
        } catch (err: any) {
            alert(err.message || "Failed to delete project");
        } finally { setIsDeleting(false); }
    };

    const readFileAsText = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.onerror = reject;
            reader.readAsText(file);
        });

    const handleAlign = async (sf: File, tf: File, sl: string, tl: string) => {
        const src = await readFileAsText(sf);
        const tgt = await readFileAsText(tf);
        return await autoAligner.align({
            sourceSegments: src.split("\n").filter(l => l.trim()),
            targetSegments: tgt.split("\n").filter(l => l.trim()),
            sourceLanguage: sl, targetLanguage: tl,
        });
    };

    const handleSaveToTM = async (alignments: any, sl: string, tl: string) => {
        if (!user) return;
        const pairs = alignments?.pairs || [];
        let saved = 0;
        for (const pair of pairs) {
            try {
                await translationMemory.addEntry({
                    source_text: pair.sourceText || pair.source,
                    target_text: pair.targetText || pair.target,
                    source_language: sl, target_language: tl,
                    user_id: user.id, context: "auto-aligned",
                });
                saved++;
            } catch {}
        }
        alert(`Saved ${saved} pairs to Translation Memory!`);
    };

    const getProgress = (p: Project) =>
        p.total_segments === 0 ? 0 : Math.round((p.completed_segments / p.total_segments) * 100);

    const getStatus = (prog: number, p?: Project) => {
        if (p && p.total_segments > 0 && p.completed_segments === p.total_segments) return "complete";
        return prog > 0 ? "in_progress" : "not_started";
    };

    const fmtLang = (code: string) => {
        const m: Record<string, string> = {
            en: "English", "en-gb": "EN (UK)", "en-us": "EN (US)",
            es: "Spanish", fr: "French", de: "German", it: "Italian",
            pt: "Portuguese", "pt-br": "PT-BR", "pt-pt": "PT-PT",
            ru: "Russian", zh: "Chinese", ja: "Japanese", ko: "Korean", ar: "Arabic",
        };
        return m[code] || code.toUpperCase();
    };

    const fmtAgo = (d: string) => {
        const h = Math.floor((Date.now() - new Date(d).getTime()) / 3600000);
        if (h < 1) return "Just now";
        if (h < 24) return `${h}h ago`;
        if (h < 168) return `${Math.floor(h / 24)}d ago`;
        return `${Math.floor(h / 168)}w ago`;
    };

    const getPairs = () =>
        Array.from(new Set(projects.map(p => `${p.source_language}-${p.target_language}`)));

    const filtered = projects.filter(p => {
        const prog = getProgress(p);
        return (
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            (selectedPair === "all" || `${p.source_language}-${p.target_language}` === selectedPair) &&
            (selectedStatus === S_ALL || getStatus(prog, p) === selectedStatus)
        );
    });

    const sc = {
        [S_DONE]: { bar: "bg-emerald-500", border: "border-l-emerald-400", pct: "text-emerald-600", badge: "bg-emerald-50 text-emerald-700 border border-emerald-200", label: "Complete",     icon: <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> },
        [S_WIP]:  { bar: "bg-gold-500",    border: "border-l-gold-400",    pct: "text-gold-600",    badge: "bg-amber-50 text-amber-700 border border-amber-200",       label: "In Progress", icon: <Clock className="h-3.5 w-3.5 text-gold-500 shrink-0" /> },
        [S_NEW]:  { bar: "bg-slate-300",   border: "border-l-slate-300",   pct: "text-slate-400",   badge: "bg-slate-50 text-slate-500 border border-slate-200",       label: "Not Started", icon: <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" /> },
    };

    const counts = {
        [S_ALL]:  projects.length,
        [S_NEW]:  projects.filter(p => getProgress(p) === 0).length,
        [S_WIP]:  projects.filter(p => { const pr = getProgress(p); return pr > 0 && pr < 100; }).length,
        [S_DONE]: projects.filter(p => getProgress(p) === 100).length,
    };

    const pairCounts = getPairs().reduce((acc, pair) => {
        acc[pair] = projects.filter(p => `${p.source_language}-${p.target_language}` === pair).length;
        return acc;
    }, {} as Record<string, number>);

    const openDelete = (id: string, name: string) =>
        setDeleteDialog({ isOpen: true, projectId: id, projectName: name });

    const ActionBtn = ({ project, progress }: { project: Project; progress: number }) => (
        <div className="flex items-center gap-1">
            <button
                onClick={e => { e.stopPropagation(); openDelete(project.id, project.name); }}
                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
            </button>
            <Link to={`/projects/${project.id}/editor`}>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap">
                    {progress > 0 ? "Continue" : "Start"} <ArrowRight className="h-3 w-3" />
                </button>
            </Link>
        </div>
    );

    if (loading) return (
        <div className="flex h-full">
            <div className="w-56 shrink-0 border-r border-gray-200 bg-white p-4 space-y-2">
                {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
            </div>
            <div className="flex-1 p-6 space-y-2">
                {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
            </div>
        </div>
    );

    return (
        <div className="flex h-full min-h-screen bg-gray-50">

            {/* ── LEFT FILTER PANEL ─────────────────────── */}
            <aside className="w-56 shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
                <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                    </div>
                </div>

                {/* Status */}
                <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Status</p>
                    {[
                        { key: S_ALL,  label: "All Projects" },
                        { key: S_NEW,  label: "Not Started" },
                        { key: S_WIP,  label: "In Progress" },
                        { key: S_DONE, label: "Complete" },
                    ].map(({ key, label }) => (
                        <button key={key} onClick={() => setSelectedStatus(key)}
                            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm transition-colors mb-0.5 ${
                                selectedStatus === key
                                    ? "bg-slate-900 text-white font-medium"
                                    : "text-slate-600 hover:bg-slate-50"
                            }`}>
                            <span>{label}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                                selectedStatus === key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"
                            }`}>{counts[key as keyof typeof counts]}</span>
                        </button>
                    ))}
                </div>

                {/* Language pairs */}
                <div className="p-3 flex-1">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Language Pair</p>
                    <button onClick={() => setSelectedPair("all")}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm transition-colors mb-0.5 ${
                            selectedPair === "all" ? "bg-slate-900 text-white font-medium" : "text-slate-600 hover:bg-slate-50"
                        }`}>
                        <span>All pairs</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                            selectedPair === "all" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"
                        }`}>{projects.length}</span>
                    </button>
                    {getPairs().map(pair => {
                        const [src, ...rest] = pair.split("-");
                        const tgt = rest.join("-");
                        return (
                            <button key={pair} onClick={() => setSelectedPair(pair)}
                                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm transition-colors mb-0.5 ${
                                    selectedPair === pair ? "bg-slate-900 text-white font-medium" : "text-slate-600 hover:bg-slate-50"
                                }`}>
                                <span className="truncate text-left flex items-center gap-1">
                                    <Globe className="h-3 w-3 shrink-0 opacity-50" />
                                    {fmtLang(src)} → {fmtLang(tgt)}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold shrink-0 ml-1 ${
                                    selectedPair === pair ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"
                                }`}>{pairCounts[pair]}</span>
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* ── MAIN CONTENT ──────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0">

                {/* Top bar */}
                <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-3 flex-1">
                        <div className="relative max-w-xs flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <input type="text" placeholder="Search projects..."
                                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold-400 focus:border-transparent bg-white" />
                        </div>
                        <span className="text-xs text-slate-400 shrink-0">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* View toggle */}
                        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
                            {([["table", List], ["grouped", SlidersHorizontal], ["grid", LayoutGrid]] as const).map(([mode, Icon]) => (
                                <button key={mode} onClick={() => setViewMode(mode)}
                                    className={`p-1.5 rounded-md transition-colors ${viewMode === mode ? "bg-white text-gold-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                                    <Icon className="h-3.5 w-3.5" />
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setAlignerOpen(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
                            <AlignJustify className="w-3.5 h-3.5" />Auto-Aligner
                        </button>
                        <Link to="/projects/new">
                            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-700 text-white text-xs font-semibold rounded-lg transition-colors">
                                <Plus className="h-3.5 w-3.5" />New Project
                            </button>
                        </Link>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5">
                    {filtered.length === 0 ? (
                        <div className="text-center py-20">
                            <FileText className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-slate-700 mb-1">No projects match your filters</p>
                            <p className="text-xs text-slate-400">Try adjusting the filters on the left.</p>
                        </div>
                    ) : viewMode === "table" ? (
                        /* TABLE VIEW */
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            {/* Table header */}
                            <div className="grid text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-gray-200 px-4 py-2.5"
                                style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,1.2fr) 80px 80px 90px 140px 120px" }}>
                                <div>Project</div>
                                <div>Language Pair</div>
                                <div className="text-right">Segments</div>
                                <div className="text-right">Words</div>
                                <div className="text-center">Status</div>
                                <div className="text-right">Progress</div>
                                <div className="text-right">Actions</div>
                            </div>
                            {/* Rows */}
                            {filtered.map((project, idx) => {
                                const prog = getProgress(project);
                                const status = getStatus(prog, project);
                                const cfg = sc[status];
                                return (
                                    <div key={project.id}
                                        className={`grid items-center px-4 py-3 border-l-4 ${cfg.border} transition-colors hover:bg-slate-50 ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                        style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,1.2fr) 80px 80px 90px 140px 120px" }}>
                                        {/* Name */}
                                        <div className="min-w-0 pr-3">
                                            <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />{fmtAgo(project.updated_at)}
                                                </span>
                                                {(project.file_count ?? 0) > 0 && (
                                                    <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded-full">
                                                        {project.file_count} file{project.file_count !== 1 ? "s" : ""}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {/* Lang pair */}
                                        <div className="text-xs text-slate-600 flex items-center gap-1 min-w-0">
                                            <Globe className="h-3 w-3 text-slate-400 shrink-0" />
                                            <span className="truncate">{fmtLang(project.source_language)} → {fmtLang(project.target_language)}</span>
                                        </div>
                                        {/* Segments */}
                                        <div className="text-sm text-slate-700 text-right font-medium">{project.total_segments.toLocaleString()}</div>
                                        {/* Words */}
                                        <div className="text-sm text-slate-700 text-right font-medium">{project.word_count.toLocaleString()}</div>
                                        {/* Status badge */}
                                        <div className="flex justify-center">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>{cfg.label}</span>
                                        </div>
                                        {/* Progress */}
                                        <div className="flex flex-col items-end gap-1">
                                            <span className={`text-sm font-bold ${cfg.pct}`}>{prog}%</span>
                                            <div className="w-24 bg-gray-100 rounded-full h-1.5">
                                                <div className={`h-1.5 rounded-full ${cfg.bar}`} style={{ width: `${prog}%` }} />
                                            </div>
                                        </div>
                                        {/* Actions */}
                                        <div className="flex justify-end">
                                            <ActionBtn project={project} progress={prog} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : viewMode === "grid" ? (
                        /* GRID VIEW */
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filtered.map(project => {
                                const prog = getProgress(project);
                                const status = getStatus(prog, project);
                                const cfg = sc[status];
                                return (
                                    <div key={project.id} className={`bg-white rounded-xl border border-gray-200 border-t-4 ${cfg.border.replace("border-l-","border-t-")} overflow-hidden hover:shadow-md transition-shadow flex flex-col`}>
                                        <div className="p-4 flex-1">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1 min-w-0 pr-2">
                                                    <h3 className="text-sm font-semibold text-slate-900 truncate">{project.name}</h3>
                                                    <span className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                        <Globe className="h-3 w-3" />{fmtLang(project.source_language)} → {fmtLang(project.target_language)}
                                                    </span>
                                                </div>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${cfg.badge}`}>{cfg.label}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 my-3 py-3 border-y border-gray-100 text-center">
                                                {[
                                                    { v: project.total_segments, l: "Segments" },
                                                    { v: project.word_count.toLocaleString(), l: "Words" },
                                                    { v: project.file_count || 0, l: "Files" },
                                                ].map(({ v, l }) => (
                                                    <div key={l}>
                                                        <div className="text-base font-bold text-slate-800">{v}</div>
                                                        <div className="text-xs text-slate-400">{l}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-slate-400">Progress</span>
                                                    <span className={`font-bold ${cfg.pct}`}>{prog}%</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div className={`h-1.5 rounded-full ${cfg.bar}`} style={{ width: `${prog}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="px-4 pb-4 flex items-center justify-between border-t border-gray-100 pt-3">
                                            <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtAgo(project.updated_at)}</span>
                                            <ActionBtn project={project} progress={prog} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* GROUPED VIEW */
                        <div className="space-y-4">
                            {(() => {
                                const groups: Record<string, Project[]> = {};
                                filtered.forEach(p => {
                                    const k = `${p.source_language}-${p.target_language}`;
                                    if (!groups[k]) groups[k] = [];
                                    groups[k].push(p);
                                });
                                return Object.entries(groups).map(([key, gps]) => {
                                    const [src, ...rest] = key.split("-");
                                    const tgt = rest.join("-");
                                    const collapsed = collapsedGroups[key];
                                    return (
                                        <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                            <button onClick={() => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }))}
                                                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 border-b border-gray-200 transition-colors">
                                                <Globe className="h-4 w-4 text-slate-400" />
                                                <span className="text-sm font-semibold text-slate-700">{fmtLang(src)} → {fmtLang(tgt)}</span>
                                                <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full font-medium">{gps.length}</span>
                                                <span className="ml-auto text-slate-400">
                                                    {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                </span>
                                            </button>
                                            {!collapsed && (
                                                <div>
                                                    {gps.map((project, idx) => {
                                                        const prog = getProgress(project);
                                                        const cfg = sc[getStatus(prog, project)];
                                                        return (
                                                            <div key={project.id}
                                                                className={`grid items-center px-4 py-3 border-l-4 ${cfg.border} hover:bg-slate-50 transition-colors ${idx !== gps.length - 1 ? "border-b border-gray-100" : ""}`}
                                                                style={{ gridTemplateColumns: "minmax(0,1fr) 80px 80px 90px 140px 120px" }}>
                                                                <div className="min-w-0 pr-3">
                                                                    <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                                                                    <span className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                                                        <Calendar className="h-3 w-3" />{fmtAgo(project.updated_at)}
                                                                        {(project.file_count ?? 0) > 0 && <span className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded-full">{project.file_count} file{project.file_count !== 1 ? "s" : ""}</span>}
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm text-slate-700 text-right font-medium">{project.total_segments.toLocaleString()}</div>
                                                                <div className="text-sm text-slate-700 text-right font-medium">{project.word_count.toLocaleString()}</div>
                                                                <div className="flex justify-center">
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>{cfg.label}</span>
                                                                </div>
                                                                <div className="flex flex-col items-end gap-1">
                                                                    <span className={`text-sm font-bold ${cfg.pct}`}>{prog}%</span>
                                                                    <div className="w-16 bg-gray-100 rounded-full h-1">
                                                                        <div className={`h-1 rounded-full ${cfg.bar}`} style={{ width: `${prog}%` }} />
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-end">
                                                                    <ActionBtn project={project} progress={prog} />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    )}
                </div>
            </div>

            <DeleteProjectDialog
                isOpen={deleteDialog.isOpen}
                projectName={deleteDialog.projectName}
                onConfirm={handleDeleteProject}
                onCancel={() => setDeleteDialog({ isOpen: false, projectId: null, projectName: "" })}
                isDeleting={isDeleting}
            />

            {alignerOpen && user && (
                <AutoAlignerDialog
                    isOpen={alignerOpen}
                    onClose={() => setAlignerOpen(false)}
                    sourceLang="en"
                    targetLang="pt-br"
                    userId={user.id}
                    projectId=""
                />
            )}
        </div>
    );
};

export default ProjectsPage;
